import { VStack } from '@chakra-ui/react';
import {
  Button,
  FieldHelpText,
  Icons,
  InputVariant,
  Notification,
  PassiveVariants,
  SvgSpriteConsumer,
} from '@pluxee-design-system/core';
import { InputControl } from '@pluxee-design-system/react-hook-form';
import { SelectControl } from 'common/forms';
import HtmlText from 'common/typography/HtmlText';
import { mapGroupToKey } from 'components/Form';
import { changeAcquirerTerminalIncompatibilityModal } from 'components/Onboarding/modals';
import {
  BelgiumOnboardingValues,
  TerminalAcquirer,
  TerminalAcquirerMidTypeEnum,
  TerminalAcquirerTidTypeEnum,
} from 'components/Onboarding/types';
import useModal from 'context/ModalContext';
import markupParser from 'html-react-parser';
import { I18nProps, withTranslations } from 'i18n';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import gtm from 'trackers/gtm';
import { ConfigField } from 'types/config';
import { template } from './be.utils';
import OnlineTerminalRow from './BeOnlineTerminalRow';

interface TerminalsProps extends I18nProps {
  config: Record<string, ConfigField>;
  disabled?: boolean;
  is4cEnabled?: boolean;
  isOnboarding?: boolean;
  terminalAcquirers: TerminalAcquirer[];
}

const OnlineTerminals = ({
  config,
  disabled,
  is4cEnabled = true,
  isOnboarding,
  terminalAcquirers,
  t,
  tHtml,
  tGroup,
}: TerminalsProps) => {
  const showModal = useModal();
  const {
    append,
    fields: valuesPOSOnline,
    remove,
    replace,
  } = useFieldArray<BelgiumOnboardingValues, 'POS'>({ name: 'POS' });
  const { formState, getValues, setValue, watch } = useFormContext<BelgiumOnboardingValues>();
  const { errors } = formState;
  const valuesTerminalAcquirerOnline = watch('terminalAcquirerOnline');
  const terminalAcquirer = useMemo(
    () => terminalAcquirers.find((a) => a.name === valuesTerminalAcquirerOnline),
    [terminalAcquirers, valuesTerminalAcquirerOnline],
  );
  const terminalProviders = useMemo(
    () => mapGroupToKey(tGroup('terminal_providers_online.terminalProviders')),
    [tGroup],
  );
  const numOfTerminals = valuesPOSOnline.length;
  const showMerchantId =
    terminalAcquirer?.mid === TerminalAcquirerMidTypeEnum.OPTIONAL ||
    terminalAcquirer?.mid === TerminalAcquirerMidTypeEnum.MANDATORY;
  const showTerminals =
    terminalAcquirer?.tid === TerminalAcquirerTidTypeEnum.OPTIONAL ||
    terminalAcquirer?.tid === TerminalAcquirerTidTypeEnum.MANDATORY;

  const handleAcquirerChange = useCallback(
    async (newAcquirer: TerminalAcquirer) => {
      const currentTerminals = getValues('POSOnline');
      if (currentTerminals.length > 1 && !newAcquirer.add_additional_terminal) {
        if (
          await showModal(changeAcquirerTerminalIncompatibilityModal(t, tHtml, newAcquirer.label))
        ) {
          replace(currentTerminals.slice(0, 1));
        } else {
          return; // go out
        }
      }
      setValue('terminalAcquirerOnline', newAcquirer.value);
    },
    [setValue, showModal, t, tHtml, replace, getValues],
  );
  const handleAddTerminal = useCallback(() => {
    if (isOnboarding) gtm.trackClickLocationAddTerminal();
    append(template(numOfTerminals.toString()));
  }, [append, isOnboarding, numOfTerminals]);

  return (
    <VStack align="stretch" spacing={4}>
      {is4cEnabled && (
        <SelectControl
          mandatory
          name="terminalAcquirerOnline"
          id="LocationStep_terminalAcquirerOnline"
          placeholder={t('4c_acquirer_whitelist.form.acquirer.placeholder', 'Select one')}
          title={t('4c_acquirer_whitelist.form.acquirer.label', 'Acquirer')}
          notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
          onChange={handleAcquirerChange}
          options={terminalAcquirers}
          sortAlphabetically={false}
        />
      )}
      {showMerchantId && (
        <InputControl
          type="text"
          mandatory={terminalAcquirer.mid === TerminalAcquirerMidTypeEnum.MANDATORY}
          name="terminalMerchantIdOnline"
          id="LocationStep_merchantIdOnline"
          placeholder={
            terminalAcquirer?.mid_placeholder ||
            t('4c_acquirer_whitelist.form.merchantId.placeholder', 'Merchant ID')
          }
          title={t('4c_acquirer_whitelist.form.merchantId.label', 'Merchant ID')}
          helpText={terminalAcquirer?.mid_help_text}
          helpTextIcon={<SvgSpriteConsumer svgId={Icons.INFO_OUTLINE16} size={16} />}
        />
      )}
      {terminalAcquirer?.instructions && (
        <Notification
          // @ts-expect-error No error, ReactNode can be passed
          description={
            <HtmlText as="div" variant="body.mediumDemibold">
              {markupParser(terminalAcquirer.instructions)}
            </HtmlText>
          }
          variant={PassiveVariants.PASSIVE_INFORMATION}
          isClosable={false}
          withoutShadow
        />
      )}
      {showTerminals && (
        <>
          <VStack align="stretch" spacing={6}>
            {valuesPOSOnline.map((field, index) => (
              <OnlineTerminalRow
                key={`POSOnline-field-${field.id}`}
                config={config}
                disabled={disabled}
                idPrefix=""
                name={`POSOnline.${index}].`}
                index={index}
                onRemove={remove}
                providers={terminalProviders}
                canRemove={terminalAcquirer?.add_additional_terminal && numOfTerminals > 1}
                isMandatory={terminalAcquirer?.tid === TerminalAcquirerTidTypeEnum.MANDATORY}
              />
            ))}
          </VStack>
          {(terminalAcquirer?.add_additional_psp || numOfTerminals === 0) && (
            <Button
              type="button"
              onClick={handleAddTerminal}
              variant="secondaryOutlined"
              id="Terminal_addTerminal"
              size="md"
              alignSelf="flex-start"
            >
              {t('location_add.buttons.addTerminal', 'Add a terminal')}
            </Button>
          )}

          {errors.POSOnline?.message && (
            <FieldHelpText
              disabled={disabled}
              text={errors.POSOnline.message as string}
              variant={InputVariant.ERROR}
            />
          )}
        </>
      )}
    </VStack>
  );
};

export default withTranslations(OnlineTerminals);
